import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Modal, Button, Select, Typography, Flex } from "antd"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { getAIDashboardWidgetCatalog } from "store/aiDashboard"
import styles from "../../style.module.css"

const { Text } = Typography

export default function AddWidgetModal({ open, onClose, onAdd, loading, activeInsightTypes }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [insightType, setInsightType] = useState(null)
  const [chartType, setChartType] = useState(null)
  const [catalog, setCatalog] = useState([])
  const [isCatalogLoading, setCatalogLoading] = useState(false)

  useEffect(() => {
    if (open) {
      setCatalogLoading(true)
      dispatch(getAIDashboardWidgetCatalog())
        .then((res) => {
          const items = res?.catalog ?? []
          setCatalog(Array.isArray(items) ? items : [])
        })
        .catch(() => setCatalog([]))
        .finally(() => setCatalogLoading(false))
    } else {
      setInsightType(null)
      setChartType(null)
      setCatalog([])
    }
  }, [open, dispatch])

  const insightTypeOptions = catalog
    .filter((item) => item.insight_type !== "ai_generated" && !activeInsightTypes?.has(item.insight_type))
    .map((item) => ({ value: item.insight_type, label: item.label }))

  const selectedCatalogItem = catalog.find((item) => item.insight_type === insightType)
  const chartTypeOptions = (selectedCatalogItem?.chart_types ?? []).map((ct) => ({
    value: ct,
    label: ct.charAt(0).toUpperCase() + ct.slice(1)
  }))

  const handleInsightTypeChange = (val) => {
    setInsightType(val)
    setChartType(null)
  }

  const handleClose = () => {
    setInsightType(null)
    setChartType(null)
    onClose()
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      width="min(600px, 92vw)"
      centered
      title={
        <div>
          <div className={styles.modalTitle}>{t("ai_dashboard.add_widget_title")}</div>
          <div className={styles.modalSubtitle}>{t("ai_dashboard.add_widget_subtitle")}</div>
        </div>
      }
    >
      <Flex vertical gap="var(--spacing-xl)" className={styles.modalBody}>
        <div>
          <Text strong className={styles.formLabel}>{t("ai_dashboard.select_insight_type")}</Text>
          <Select
            placeholder={t("ai_dashboard.choose_insight")}
            options={insightTypeOptions}
            value={insightType}
            onChange={handleInsightTypeChange}
            loading={isCatalogLoading}
            className={styles.fullWidth}
            size="large"
          />
        </div>

        <div>
          <Text strong className={styles.formLabel}>{t("ai_dashboard.select_chart_type")}</Text>
          <Select
            placeholder={insightType ? t("ai_dashboard.choose_option") : t("ai_dashboard.select_insight_first")}
            options={chartTypeOptions}
            value={chartType}
            onChange={setChartType}
            disabled={!insightType}
            className={styles.fullWidth}
            size="large"
          />
        </div>

        <Flex justify="flex-end" gap={8} className={styles.modalFooter}>
          <Button shape="round" onClick={handleClose} disabled={loading}>{t("ai_dashboard.cancel")}</Button>
          <Button type="primary" shape="round" disabled={!insightType || !chartType} loading={loading} onClick={() => onAdd({ insightType, chartType })}>
            {t("ai_dashboard.add_widget")}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}

AddWidgetModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func,
  loading: PropTypes.bool,
  activeInsightTypes: PropTypes.instanceOf(Set),
}
